import React, { useState } from 'react';
import { Modal, Button, Badge, Row, Col } from 'react-bootstrap';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

/**
 * LearnAttackModal Component - Modern Design
 * 
 * Shows when a Pokemon levels up and can learn a new attack.
 * Beautiful glassmorphism design with smooth animations.
 */
const LearnAttackModal = ({ show, onHide, pendingAttackData, onAttackLearned }) => {
    const [loading, setLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [currentAttackIndex, setCurrentAttackIndex] = useState(0);

    if (!pendingAttackData || !pendingAttackData.pending_attacks || pendingAttackData.pending_attacks.length === 0) {
        return null;
    }

    const currentPendingAttack = pendingAttackData.pending_attacks[currentAttackIndex];
    const currentAttacks = pendingAttackData.current_attacks || [];
    const hasEmptySlot = currentAttacks.length < 4;

    // Modern type colors with gradients
    const typeColors = {
        Normal: { bg: 'linear-gradient(135deg, #A8A878 0%, #8a8a5c 100%)', color: '#A8A878' },
        Fire: { bg: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)', color: '#FF6B35' },
        Water: { bg: 'linear-gradient(135deg, #4A90D9 0%, #1E88E5 100%)', color: '#4A90D9' },
        Electric: { bg: 'linear-gradient(135deg, #FFD93D 0%, #F4C430 100%)', color: '#FFD93D' },
        Grass: { bg: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)', color: '#4CAF50' },
        Ice: { bg: 'linear-gradient(135deg, #81D4FA 0%, #4FC3F7 100%)', color: '#81D4FA' },
        Fighting: { bg: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)', color: '#D32F2F' },
        Poison: { bg: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)', color: '#9C27B0' },
        Ground: { bg: 'linear-gradient(135deg, #D4A574 0%, #A67B5B 100%)', color: '#D4A574' },
        Flying: { bg: 'linear-gradient(135deg, #90CAF9 0%, #64B5F6 100%)', color: '#90CAF9' },
        Psychic: { bg: 'linear-gradient(135deg, #EC407A 0%, #D81B60 100%)', color: '#EC407A' },
        Bug: { bg: 'linear-gradient(135deg, #8BC34A 0%, #689F38 100%)', color: '#8BC34A' },
        Rock: { bg: 'linear-gradient(135deg, #A1887F 0%, #795548 100%)', color: '#A1887F' },
        Ghost: { bg: 'linear-gradient(135deg, #7E57C2 0%, #5E35B1 100%)', color: '#7E57C2' },
        Dragon: { bg: 'linear-gradient(135deg, #7C4DFF 0%, #651FFF 100%)', color: '#7C4DFF' },
        Dark: { bg: 'linear-gradient(135deg, #5D4037 0%, #3E2723 100%)', color: '#5D4037' },
        Steel: { bg: 'linear-gradient(135deg, #90A4AE 0%, #607D8B 100%)', color: '#90A4AE' },
        Fairy: { bg: 'linear-gradient(135deg, #F48FB1 0%, #EC407A 100%)', color: '#F48FB1' }
    };

    const getTypeStyle = (type) => typeColors[type] || { bg: 'linear-gradient(135deg, #888 0%, #666 100%)', color: '#888' };
    
    const getCategoryStyle = (category) => {
        if (category === 'Physical') return { icon: '⚔️', color: '#FF5722', label: 'Physical' };
        if (category === 'Special') return { icon: '🔮', color: '#9C27B0', label: 'Special' };
        return { icon: '📊', color: '#607D8B', label: 'Status' };
    };

    const handleLearnAttack = async (replaceSlot = null) => {
        setLoading(true);
        try {
            // Debug: log token
            const token = localStorage.getItem('session_token') || localStorage.getItem('token');
            console.log('[LEARN ATTACK] Token:', token ? 'EXISTS' : 'MISSING');
            
            const response = await axios.post('/api/api/checkPendingAttacks.php', {
                pokemon_id: pendingAttackData.pokemon_id,
                new_attack: currentPendingAttack.name,
                replace_slot: replaceSlot,
                skip_learning: false
            });

            if (response.data.success) {
                toast.success(`${pendingAttackData.pokemon_name} learned ${currentPendingAttack.name}!`, {
                    icon: '✨'
                });
                
                if (currentAttackIndex < pendingAttackData.pending_attacks.length - 1) {
                    setCurrentAttackIndex(currentAttackIndex + 1);
                    setSelectedSlot(null);
                } else {
                    if (onAttackLearned) onAttackLearned();
                    onHide();
                }
            } else {
                toast.error(response.data.error || 'Failed to learn attack');
            }
        } catch (error) {
            console.error('Learn attack error:', error);
            console.error('[LEARN ATTACK] Response status:', error.response?.status);
            console.error('[LEARN ATTACK] Response data:', error.response?.data);
            toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to learn attack');
        } finally {
            setLoading(false);
        }
    };

    const handleSkipAttack = async () => {
        setLoading(true);
        try {
            // Debug: log token
            const token = localStorage.getItem('session_token') || localStorage.getItem('token');
            console.log('[SKIP ATTACK] Token:', token ? 'EXISTS' : 'MISSING');
            
            await axios.post('/api/api/checkPendingAttacks.php', {
                pokemon_id: pendingAttackData.pokemon_id,
                new_attack: currentPendingAttack.name,
                skip_learning: true
            });

            toast.info(`${pendingAttackData.pokemon_name} did not learn ${currentPendingAttack.name}.`);
            
            if (currentAttackIndex < pendingAttackData.pending_attacks.length - 1) {
                setCurrentAttackIndex(currentAttackIndex + 1);
                setSelectedSlot(null);
            } else {
                onHide();
            }
        } catch (error) {
            console.error('Skip attack error:', error);
        } finally {
            setLoading(false);
        }
    };

    const typeStyle = getTypeStyle(currentPendingAttack.type);
    const categoryStyle = getCategoryStyle(currentPendingAttack.category);

    // Custom styles
    const styles = {
        modalContent: {
            background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            border: 'none',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        },
        header: {
            background: 'transparent',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            padding: '24px 32px',
            position: 'relative'
        },
        headerGlow: {
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60%',
            height: '2px',
            background: typeStyle.bg,
            filter: 'blur(8px)'
        },
        newMoveCard: {
            background: `linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${typeStyle.color}`,
            borderRadius: '20px',
            padding: '24px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 0 40px ${typeStyle.color}40`
        },
        newMoveGlow: {
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: `radial-gradient(circle, ${typeStyle.color}20 0%, transparent 50%)`,
            animation: 'pulse 2s infinite'
        },
        statBox: {
            background: 'rgba(0,0,0,0.3)',
            borderRadius: '12px',
            padding: '12px',
            textAlign: 'center'
        },
        currentMoveCard: (isSelected) => ({
            background: isSelected 
                ? 'linear-gradient(135deg, rgba(220, 53, 69, 0.3) 0%, rgba(220, 53, 69, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
            backdropFilter: 'blur(10px)',
            border: isSelected ? '2px solid #dc3545' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px',
            padding: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            transform: isSelected ? 'scale(1.02)' : 'scale(1)'
        }),
        actionButton: {
            borderRadius: '14px',
            padding: '14px 28px',
            fontWeight: '600',
            fontSize: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            transition: 'all 0.3s ease'
        },
        learnButton: {
            background: 'linear-gradient(135deg, #00C853 0%, #00E676 100%)',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 200, 83, 0.4)'
        },
        replaceButton: {
            background: 'linear-gradient(135deg, #FF5252 0%, #FF1744 100%)',
            border: 'none',
            boxShadow: '0 4px 20px rgba(255, 82, 82, 0.4)'
        },
        skipButton: {
            background: 'transparent',
            border: '2px solid rgba(255,255,255,0.3)',
            color: 'rgba(255,255,255,0.7)'
        }
    };

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            centered 
            size="lg" 
            backdrop="static"
            contentClassName="border-0"
        >
            <div style={styles.modalContent}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerGlow}></div>
                    <div className="text-center">
                        <span style={{ fontSize: '3rem' }}>🎉</span>
                        <h4 className="text-white mt-2 mb-1" style={{ fontWeight: '700' }}>
                            {pendingAttackData.pokemon_name}
                        </h4>
                        <p className="text-white-50 mb-0">wants to learn a new move!</p>
                    </div>
                </div>

                {/* Body */}
                <Modal.Body className="p-4">
                    {/* New Move Display */}
                    <div style={styles.newMoveCard} className="mb-4">
                        <div style={styles.newMoveGlow}></div>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <Badge 
                                    style={{ 
                                        background: typeStyle.bg,
                                        padding: '8px 16px',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        borderRadius: '20px'
                                    }}
                                >
                                    {currentPendingAttack.type}
                                </Badge>
                                <Badge 
                                    style={{ 
                                        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                        color: '#000',
                                        padding: '6px 12px',
                                        fontSize: '0.8rem',
                                        fontWeight: '700',
                                        borderRadius: '20px'
                                    }}
                                >
                                    ✨ NEW
                                </Badge>
                            </div>
                            
                            <h2 className="text-white mb-4" style={{ fontWeight: '700', fontSize: '1.8rem' }}>
                                {currentPendingAttack.name}
                            </h2>
                            
                            <Row className="g-3">
                                <Col xs={3}>
                                    <div style={styles.statBox}>
                                        <div className="text-white-50 small mb-1">POWER</div>
                                        <div className="text-white fw-bold" style={{ fontSize: '1.5rem', color: '#FF5722' }}>
                                            {currentPendingAttack.power || '—'}
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={3}>
                                    <div style={styles.statBox}>
                                        <div className="text-white-50 small mb-1">ACCURACY</div>
                                        <div className="text-white fw-bold" style={{ fontSize: '1.5rem', color: '#4CAF50' }}>
                                            {currentPendingAttack.accuracy || '—'}%
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={3}>
                                    <div style={styles.statBox}>
                                        <div className="text-white-50 small mb-1">PP</div>
                                        <div className="text-white fw-bold" style={{ fontSize: '1.5rem', color: '#2196F3' }}>
                                            {currentPendingAttack.pp || '—'}
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={3}>
                                    <div style={styles.statBox}>
                                        <div className="text-white-50 small mb-1">CATEGORY</div>
                                        <div style={{ fontSize: '1.5rem' }}>
                                            {categoryStyle.icon}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {hasEmptySlot ? (
                        <div 
                            className="text-center p-4"
                            style={{ 
                                background: 'linear-gradient(135deg, rgba(0, 200, 83, 0.2) 0%, rgba(0, 200, 83, 0.05) 100%)',
                                borderRadius: '16px',
                                border: '1px solid rgba(0, 200, 83, 0.3)'
                            }}
                        >
                            <span style={{ fontSize: '2rem' }}>✅</span>
                            <p className="text-white mb-0 mt-2">
                                <strong>{pendingAttackData.pokemon_name}</strong> has an empty move slot!
                                <br/>
                                <span className="text-white-50">The new move will be added automatically.</span>
                            </p>
                        </div>
                    ) : (
                        <>
                            <div 
                                className="text-center p-3 mb-3"
                                style={{ 
                                    background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.2) 0%, rgba(255, 193, 7, 0.05) 100%)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255, 193, 7, 0.3)'
                                }}
                            >
                                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                                <p className="text-white mb-0 mt-1">
                                    <strong>{pendingAttackData.pokemon_name}</strong> already knows 4 moves!
                                    <br/>
                                    <small className="text-white-50">Select a move to replace</small>
                                </p>
                            </div>
                            
                            <Row className="g-3">
                                {currentAttacks.map((attack, idx) => {
                                    const isSelected = selectedSlot === idx + 1;
                                    const attackObj = typeof attack === 'object' ? attack : { name: attack };
                                    const attackTypeStyle = getTypeStyle(attackObj.type);
                                    const attackCategoryStyle = getCategoryStyle(attackObj.category);
                                    
                                    return (
                                        <Col md={6} key={idx}>
                                            <div 
                                                style={styles.currentMoveCard(isSelected)}
                                                onClick={() => setSelectedSlot(idx + 1)}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = isSelected ? 'scale(1.02)' : 'scale(1)'}
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <Badge 
                                                        style={{ 
                                                            background: 'rgba(255,255,255,0.2)',
                                                            fontSize: '0.7rem'
                                                        }}
                                                    >
                                                        Slot {idx + 1}
                                                    </Badge>
                                                    <Badge 
                                                        style={{ 
                                                            background: attackTypeStyle.bg,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    >
                                                        {attackObj.type || '?'}
                                                    </Badge>
                                                </div>
                                                
                                                <h6 className="text-white mb-2" style={{ fontWeight: '600' }}>
                                                    {attackObj.name || attack}
                                                </h6>
                                                
                                                <div className="d-flex justify-content-between text-white-50 small">
                                                    <span>⚡ {attackObj.power || '—'}</span>
                                                    <span>🎯 {attackObj.accuracy || '—'}%</span>
                                                    <span>{attackCategoryStyle.icon}</span>
                                                </div>
                                                
                                                {isSelected && (
                                                    <div 
                                                        className="text-center mt-2 pt-2"
                                                        style={{ borderTop: '1px solid rgba(220, 53, 69, 0.5)' }}
                                                    >
                                                        <span style={{ color: '#dc3545', fontWeight: '600', fontSize: '0.8rem' }}>
                                                            🗑️ WILL BE REPLACED
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </>
                    )}

                    {/* Progress indicator */}
                    {pendingAttackData.pending_attacks.length > 1 && (
                        <div className="text-center mt-4">
                            <div className="d-flex justify-content-center gap-2">
                                {pendingAttackData.pending_attacks.map((_, idx) => (
                                    <div 
                                        key={idx}
                                        style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: idx === currentAttackIndex 
                                                ? 'linear-gradient(135deg, #00C853 0%, #00E676 100%)'
                                                : 'rgba(255,255,255,0.3)'
                                        }}
                                    />
                                ))}
                            </div>
                            <small className="text-white-50 mt-2 d-block">
                                Move {currentAttackIndex + 1} of {pendingAttackData.pending_attacks.length}
                            </small>
                        </div>
                    )}
                </Modal.Body>

                {/* Footer */}
                <Modal.Footer 
                    className="border-0 p-4" 
                    style={{ background: 'rgba(0,0,0,0.2)' }}
                >
                    <div className="d-flex justify-content-between w-100 gap-3">
                        <Button 
                            onClick={handleSkipAttack}
                            disabled={loading}
                            style={{ ...styles.actionButton, ...styles.skipButton }}
                            className="flex-grow-1"
                        >
                            ❌ Don't Learn
                        </Button>
                        
                        {hasEmptySlot ? (
                            <Button 
                                onClick={() => handleLearnAttack(null)}
                                disabled={loading}
                                style={{ ...styles.actionButton, ...styles.learnButton }}
                                className="flex-grow-1"
                            >
                                {loading ? (
                                    <span>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Learning...
                                    </span>
                                ) : (
                                    `✨ Learn ${currentPendingAttack.name}`
                                )}
                            </Button>
                        ) : (
                            <Button 
                                onClick={() => handleLearnAttack(selectedSlot)}
                                disabled={loading || !selectedSlot}
                                style={{ 
                                    ...styles.actionButton, 
                                    ...(selectedSlot ? styles.replaceButton : { ...styles.skipButton, opacity: 0.5 })
                                }}
                                className="flex-grow-1"
                            >
                                {loading ? (
                                    <span>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Replacing...
                                    </span>
                                ) : selectedSlot ? (
                                    `🔄 Replace Slot ${selectedSlot}`
                                ) : (
                                    'Select a move to replace'
                                )}
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </div>

            {/* CSS Animation */}
            <style>{`
                @keyframes pulse {
                    0% { opacity: 0.5; transform: rotate(0deg); }
                    50% { opacity: 1; }
                    100% { opacity: 0.5; transform: rotate(360deg); }
                }
            `}</style>
        </Modal>
    );
};

export default LearnAttackModal;
